import { CommonModule } from '@angular/common';
import { Component, EventEmitter, OnInit, Output, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ChartComponent, NgApexchartsModule } from 'ng-apexcharts';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { BillingService } from '../../../../services/billing.service';
import { LookupService } from '../../../../services/lookup.service';
import { ListTableComponent } from '../../../../shared/components/list-table/list-table.component';
import { Router } from '@angular/router';
import { UserService } from '../../../../services/user.service';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { ReportFiltersComponent } from '../report-filters/report-filters.component';

@Component({
  selector: 'ppl-ignored-timesheet-report',
  standalone: true,
  imports: [
    NgApexchartsModule,
    NzCheckboxModule,
    CommonModule,
    FormsModule,
    NzCardModule,
    NzGridModule,
    NzSelectModule,
    ListTableComponent,
    NzRadioModule,
    ReportFiltersComponent
  ],
  templateUrl: './ignored-timesheet-report.component.html',
  styleUrl: './ignored-timesheet-report.component.css',
})
export class IgnoredTimesheetReportComponent implements OnInit {
  @ViewChild('chart') chart!: ChartComponent;
  @Output() metricChanged = new EventEmitter<any>();
  mcoList: any;
  programName: any;
  mcoName: any;
  selectedClaimType: any = 'All';
  claimTypes = ['All', 'SC', 'PMPM'];
  chartOptions: any;
  ignoredTimesheetReportData: any;
  series: any = [];
  labels: any = [];
  ignoreReason = 'All';
  ignoreReasonList: any[] = [];
  metricType = 'IGNORE_REASON';
  showMCO = true;
  cols: any[] = [];
  filterFields = [
    { field: 'metricType', show: true, default: 'IGNORE_REASON' },
    { field: 'mcoName', show: true, default: '' },
    { field: 'ignoreReason', show: true, default: 'All' },
  ];

  constructor(
    private lookupService: LookupService,
    private billingService: BillingService,
    private router: Router,
    private userService: UserService
  ) {}

  ngOnInit() {
    const selectedProgram = this.userService.getSelectedProgram();
    this.programName = selectedProgram?.program_code ?? '';
    const navigationState = window.history.state;
    if (navigationState.mco) {
      this.mcoName = navigationState.mco;
    }
    if (navigationState.pmpm) {
      this.selectedClaimType = navigationState.pmpm;
    }
    this.billingService
      .getIgnoreReasonDropdown(this.programName)
      .subscribe((data: any) => {
        const ignoreReasonList = data?.responsedata;
        this.ignoreReasonList = [{ ignoreReason: 'All' }, ...ignoreReasonList];
      });
    this.lookupService.getOrgData(this.programName).subscribe((data: any) => {
      if (data.status === 200) {
          this.mcoList = data.responsedata.filter((item: any) => item.orgTypeLookup === 'MCO');
          this.mcoName = this.mcoList[0]?.orgName;
          this.filterFields = this.filterFields?.map(f =>
            f.field === 'mcoName' ? { ...f, default: this.mcoList[0]?.orgName } : f
          ) ?? [];
        this.getIgnoredTimehseetData();
      }
    });
  }

  getIgnoredTimehseetData() {
    const options = {
      reportName: 'fetchIgnoreTimeSheetReport',
      mcoName: this.mcoName,
      splitBy: this.metricType,
      ignoreReason: this.ignoreReason,
      programCode: this.programName,
    };
    this.billingService
      .getReports(options)
      .subscribe((data: any) => {
        this.ignoredTimesheetReportData = data.responsedata || [];
        this.getDataToDisplay(this.ignoredTimesheetReportData);
        this.fetchChartOptions();
      });
  }

  filterChanged(change: any) {
    this.mcoName = change.formValues.mcoName;
    this.selectedClaimType = change.formValues.selectedClaimType;
    this.metricType = change.formValues.metricType;
    this.ignoreReason = change.formValues.ignoreReason;
    if (change.field === 'mcoName' || change.field === 'ignoreReason') {
      this.onSearchChange();
    } else if (change.field === 'metricType') {
      this.onMetricChange();
    } else {
      return;
    }
  }

  getDataToDisplay(data: any) {
    this.series = data.map((item: any) => item.count);
    const isMCO = this.metricType === 'MCO';
    this.labels = data.map((item: any) =>
      isMCO ? item.mcoName : item.ignoreReason
    );
    this.cols = [
      {
        field: !isMCO ? 'ignoreReason' : 'mcoName',
        header: !isMCO ? 'Ignore Reason' : 'MCO',
        jsonpath: !isMCO ? 'ignoreReason' : 'mcoName',
        isDisplayedAtHeader: 'y',
        isSearchable: 'n',
      },
      {
        field: 'count',
        header: 'Count',
        jsonpath: 'count',
        isDisplayedAtHeader: 'y',
        isSearchable: 'n',
      },
    ];
  }

  onSearchChange() {
    this.getIgnoredTimehseetData();
  }

  onMetricChange() {
    if (this.metricType === 'MCO') {
      this.metricChanged.emit(false);
    } else {
      this.metricChanged.emit(true);
    }
    this.mcoName = this.mcoList[0].orgName;
    this.ignoreReason = 'All';
    this.getIgnoredTimehseetData();
  }

  generateColors(count: number): string[] {
    const colors: string[] = [];
    const goldenAngle = 137.508;
    for (let i = 0; i < count; i++) {
      const hue = (i * goldenAngle) % 360;
      colors.push(`hsl(${hue}, 55%, 60%)`);
    }
    return colors;
  }

  getTotal(): string {
    const total = this.ignoredTimesheetReportData?.reduce(
      (sum: any, item: any) => {
        const value = item.count || 0;
        return sum + value;
      },
      0
    );
    return total.toLocaleString();
  }

  fetchChartOptions() {
    const maxLabelLength = 40;
    const truncatedLabels = this.labels.map((label: any) =>
      label.length > maxLabelLength
        ? label.slice(0, maxLabelLength) + '...'
        : label
    );

    const dynamicColors = this.generateColors(this.series.length);
    if (this.series?.length && this.labels?.length) {
      this.chartOptions = {
        series: this.series,
        chart: {
          type: 'pie',
          height: 400,
          fontFamily: 'inherit',
          toolbar: {
            show: true,
            tools: {
              download: true,
            },
            offsetY: -10,
          },
          noData: {
            text: 'No data available',
            align: 'center',
            verticalAlign: 'middle',
            offsetX: 0,
            offsetY: 0,
            style: {
              color: '#999',
              fontSize: '16px',
              fontFamily: 'Arial',
            },
          },
          events: {
            dataPointSelection: (
              event: any,
              chartContext: any,
              config: any
            ) => {
              const label = this.labels[config.dataPointIndex];
              const value = this.series[config.dataPointIndex];
              setTimeout(() => {
                this.router.navigate(['/home/viewIgnoredTimesheetsList'], {
                  state: {
                    reason: this.metricType !== 'MCO' ? label : this.ignoreReason,
                    count: value,
                    mco: this.metricType === 'MCO' ? label : this.mcoName,
                  },
                });
              });
            },
          },
        },
        title: {
          text: `Total Ignored Timesheets: ${this.getTotal()}`,
          align: 'right',
          offsetY: 20,
        },
        colors: dynamicColors,
        labels: truncatedLabels,
        legend: {
          position: 'bottom',
          fontSize: '12px',
          itemMargin: {
            horizontal: 10,
            vertical: 5,
          },
          formatter: function (seriesName: string, opts: any) {
            return seriesName;
          },
        },
        tooltip: {
          y: {
            formatter: (value: number, { seriesIndex }: any) => {
              return `${this.labels[seriesIndex]}: ${value}`;
            },
          },
        },
        responsive: [
          {
            breakpoint: 768,
            options: {
              chart: {
                width: 300,
                height: 400,
              },
              legend: {
                position: 'bottom',
                fontSize: '10px',
              },
            },
          },
        ],
      };
    }
  }

  rowClicked(event: any) {
    this.router.navigate(['/home/viewIgnoredTimesheetsList'], {
      state: {
        reason: this.metricType === 'IGNORE_REASON' ? event.ignoreReason : this.ignoreReason,
        count: event.count,
        mco: this.metricType === 'MCO' ? event.mcoName : this.mcoName,
      },
    });
  }
}
